/**
 * renumberReferences — Bug-fix Wave A (reference numbering contiguity).
 *
 * Symptom: inline `[Ref N]` markers in the report body read out of numeric
 * order — e.g. `[Ref 1, Ref 2, Ref 3]` … `[Ref 6]` … `[Ref 4, Ref 5]`.
 *
 * Root cause: at generation time the `references[]` array is assembled in a
 * FIXED canonical class order (provider rows, then knowledge-base, then
 * ADAMS, then site-research, then literature — see `assembleReferences.ts`)
 * and each inline `[Ref N]` token resolves N to the ref's POSITION in that
 * assembled array (`siteRagService.refIndex`). But the narrative cites those
 * refs in READING order, which interleaves the classes. So the body cites a
 * knowledge-base ref (high assembled index) before a later provider ref
 * (lower assembled index), and the visible numbering jumps around.
 *
 * Fix: a single pure finalize pass that renumbers references by FIRST
 * APPEARANCE in the body. The first `[Ref N]` token encountered (reading the
 * report's narrative fields in a deterministic order) becomes `[Ref 1]`, the
 * next distinct one `[Ref 2]`, and so on — contiguous and reading-order
 * consistent. References never cited keep their relative order and are
 * appended after the cited ones, so the array length and every row's payload
 * are preserved (only the ORDER and the inline TOKEN numbers change, in
 * lock-step, so every cross-reference still resolves).
 *
 * The pass is pure: it returns a NEW object and does not mutate its input.
 */
import type { CitationAudit, DataSource, SiteRagReport } from '../types/siteRag';
import {
  NUMBERED_REF_TOKEN_RE,
  collapseAdjacentDuplicateRefTokens,
  pickStrongerRefQualification,
} from './citationTokens';

/** A renumbering plan: old 1-based index → new 1-based index, plus the
 *  reordered references array. */
export interface RenumberPlan {
  /** old (1-based) → new (1-based). Every ref has an entry. */
  oldToNew: Map<number, number>;
  /** references reordered so position `new-1` holds the old ref. */
  references: DataSource[];
  /** APPENDIX-POPULATE — the set of NEW (1-based) indices that appeared in
   *  the body (the cited block). A ref present here was demonstrably cited
   *  in the FINAL renumbered narrative, so its `citedInThisReport` is
   *  re-derived to `true` regardless of what the earlier pre-renumber
   *  markCitations pass concluded. */
  citedNewIndices: Set<number>;
}

/*
 * `Ref\s*(?::\s*)?` — TOLERATE THE COLON FORM (2026-08-14).
 *
 * These patterns demanded whitespace after "Ref", so a `[Ref: 12]` token was
 * invisible to the renumberer: the tokens it COULD see were compacted to new
 * indices while the colon tokens kept their OLD ones, leaving them pointing at
 * a different source with no error anywhere. Proven end-to-end:
 *   "...B [Ref 2] and C [Ref 3]. ...B [Ref: 2] and C [Ref: 3]."
 *   -> "...B [Ref 1] and C [Ref 2]. ...B [Ref: 2] and C [Ref: 3]."
 * with references reordered to [B, C, A] — so `[Ref: 2]` now resolves to C.
 *
 * `citationFinalize` normalises the punctuation at the generation boundary, so
 * in the normal path these never see a colon. This is the SAFETY NET for any
 * body that reaches the renumberer by another route (a persisted revision, a
 * rerun overlay): tolerating the form is strictly better than silently
 * mis-numbering it.
 */
// Tagged and plain citations must share one numbering plan. Ignoring a
// [verified Ref N] token while reordering references silently changes its source.

/**
 * Collect the first-appearance order of `[Ref N]` numbers from a list of
 * narrative strings, in the order the strings are supplied. Compound tokens
 * (`[Ref 1, Ref 2]`) contribute each of their numbers left-to-right.
 *
 * Returns distinct 1-based old indices in first-appearance order.
 */
export function collectRefAppearanceOrder(texts: Array<string | null | undefined>): number[] {
  const order: number[] = [];
  const seen = new Set<number>();
  const pushN = (raw: string): void => {
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) return;
    if (seen.has(n)) return;
    seen.add(n);
    order.push(n);
  };
  for (const text of texts) {
    if (!text) continue;
    // Walk the string in positional order so compound + singular tokens are
    // discovered in true reading order. We scan with a single combined regex
    // that matches either a compound group or a singular token, in order.
    const re = new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const matched = m[0];
      // A compact group can omit the repeated Ref label. The complete token
      // already matched a strict citation grammar, so each number is a ref.
      for (const im of matched.matchAll(/\d+/g)) pushN(im[0]);
    }
  }
  return order;
}

/**
 * Build a renumbering plan from the body appearance order and the full
 * references array.
 *
 * Numbering rules (all 1-based):
 *   1. Cited refs (those whose old index appears in `appearanceOrder`, in
 *      bounds) get new indices 1..k in appearance order.
 *   2. Out-of-bounds appearance indices are ignored (the
 *      `rewriteOutOfBoundsRefs` pass handles dangling tokens separately).
 *   3. Uncited refs keep their original relative order and are appended
 *      after the cited block, so the array length is unchanged.
 */
export function buildRenumberPlan(
  appearanceOrder: number[],
  references: DataSource[],
): RenumberPlan {
  const n = references.length;
  const oldToNew = new Map<number, number>();
  const newOrderOldIdx: number[] = []; // old 1-based indices in new order

  const placed = new Set<number>();
  for (const oldIdx of appearanceOrder) {
    if (oldIdx < 1 || oldIdx > n) continue; // out of bounds — skip
    if (placed.has(oldIdx)) continue;
    placed.add(oldIdx);
    newOrderOldIdx.push(oldIdx);
  }
  // Append uncited refs in their original order.
  for (let oldIdx = 1; oldIdx <= n; oldIdx++) {
    if (placed.has(oldIdx)) continue;
    newOrderOldIdx.push(oldIdx);
  }

  const reordered: DataSource[] = [];
  const citedNewIndices = new Set<number>();
  newOrderOldIdx.forEach((oldIdx, i) => {
    oldToNew.set(oldIdx, i + 1);
    reordered.push(references[oldIdx - 1]!);
    // The first `placed.size` entries are the in-bounds refs that appeared
    // in the body, in appearance order; the rest are uncited tails appended
    // afterwards. `placed.has(oldIdx)` is the ground truth for "cited".
    if (placed.has(oldIdx)) citedNewIndices.add(i + 1);
  });

  return { oldToNew, references: reordered, citedNewIndices };
}

/**
 * Rewrite every `[Ref N]` (singular AND compound) token in a string using
 * the `oldToNew` map. Tokens whose N is not in the map (out of bounds) are
 * left untouched so a downstream out-of-bounds scrubber can handle them.
 * Compound tokens are renumbered per-member, preserving the compound form.
 */
export function rewriteRefTokens(text: string, oldToNew: Map<number, number>): string {
  if (!text) return text ?? '';
  const remap = (raw: string): string => {
    const old = Number.parseInt(raw, 10);
    const next = oldToNew.get(old);
    return next != null ? String(next) : raw;
  };
  // Handle compound tokens first (they contain multiple `Ref N`), then any
  // remaining singular tokens. Process in one combined pass so positions
  // don't shift mid-rewrite.
  const re = new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi');
  const bracketRewritten = text.replace(re, full => {
    const members = full.slice(1, -1).split(/\s*,\s*/);
    const rewritten: string[] = [];
    const seenByNum = new Map<string, { idx: number; qual: string }>();
    for (const member of members) {
      // The shared grammar has already validated the whole bracket. Preserve
      // each explicit reliability label while normalizing the Ref punctuation.
      const match = /^(?:(verified|inferred|unverified)\s+)?(?:Ref\s*(?::\s*)?)?(\d+)$/i.exec(member.trim());
      if (!match) {
        rewritten.push(member);
        continue;
      }
      const nextNum = remap(match[2]);
      const qual = match[1]?.toLowerCase() ?? '';
      const existing = seenByNum.get(nextNum);
      if (existing) {
        const stronger = pickStrongerRefQualification(existing.qual, qual);
        existing.qual = stronger;
        rewritten[existing.idx] = `${stronger ? `${stronger} ` : ''}Ref ${nextNum}`;
        continue;
      }
      seenByNum.set(nextNum, { idx: rewritten.length, qual });
      rewritten.push(`${qual ? `${qual} ` : ''}Ref ${nextNum}`);
    }
    return `[${rewritten.join(', ')}]`;
  });
  // Also remap unbracketed prose citations (`in Ref 118`, `per Ref 118`, `see Ref 118`)
  // when `oldToNew` maps the old ordinal so pre-renumber ordinals never leak (`DC-ART-016` Finding 4).
  const proseRefRewritten = bracketRewritten.replace(
    /(?<!\[)\b(in|per|see|under|from)\s+(?:(verified|inferred|unverified)\s+)?Ref\s*(?::\s*)?(\d+)\b(?!\])/gi,
    (full, prep: string, qual: string | undefined, numRaw: string) => {
      const old = Number.parseInt(numRaw, 10);
      const next = oldToNew.get(old);
      if (next == null) return full;
      const qualPrefix = qual ? `${qual.toLowerCase()} ` : '';
      return `${prep} [${qualPrefix}Ref ${next}]`;
    },
  );
  return collapseAdjacentDuplicateRefTokens(proseRefRewritten);
}

/**
 * Deep-rewrite `[Ref N]` tokens across an arbitrary JSON-shaped value
 * (string / array / object). String leaves are remapped; structure is
 * preserved. Returns a structural copy — the input is not mutated.
 */
export function rewriteRefTokensDeep<T>(value: T, oldToNew: Map<number, number>): T {
  const walk = (v: unknown, parentKey?: string): unknown => {
    if (typeof v === 'string') return rewriteRefTokens(v, oldToNew);
    if (Array.isArray(v)) return v.map(item => walk(item, parentKey));
    if (v != null && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        // This audit owns its immutable referencesAtReview numbering.
        // Rewriting its raw candidates would change the recorded evidence.
        out[k] = (k === 'gridLedStandbyPlanningAtGen' || k === 'inactiveGenerationImpactAuthorityAtGen') || (parentKey === 'qaFindings' && (k === 'sourceRoleReview' || k === 'finalSourceReview')) ? val : walk(val, k);
      }
      const evidence = findingEvidence(v as Record<string, unknown>);
      if (evidence) {
        out.evidence = {
          ...(out.evidence as Record<string, unknown>),
          refIds: Array.from(new Set(evidence.refIds.map(n => oldToNew.get(n) ?? n))),
        };
      }
      return out;
    }
    return v;
  };
  return walk(value) as T;
}

/** Numeric FindingClaim citations are positions in this report's reference
 * list, just like inline tokens. Other numeric IDs are not citations. */
function findingEvidence(value: Record<string, unknown>): { refIds: number[] } | undefined {
  if (typeof value.claim !== 'string'
    || !['confirmed', 'likely_present_unverified', 'unknown'].includes(String(value.evidenceState))) return;
  const evidence = value.evidence;
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return;
  const refIds = (evidence as Record<string, unknown>).refIds;
  if (!Array.isArray(refIds) || !refIds.every(n => typeof n === 'number' && Number.isInteger(n))) return;
  return { refIds };
}

/**
 * Remap a `ReportClaim.sources` entry of the form `ref:N` (or `Ref N`
 * label) to its new index. Non-ref sources (`doc:…`, `sed:…`, …) pass
 * through unchanged.
 */
export function remapSourceToken(source: string, oldToNew: Map<number, number>): string {
  if (!source) return source;
  // `ref:3` form.
  const colon = /^ref:(\d+)$/i.exec(source.trim());
  if (colon) {
    const next = oldToNew.get(Number(colon[1]));
    return next != null ? `ref:${next}` : source;
  }
  // `Ref 3` label form (used by keyFindingsDerive.formatSourceLabel).
  const label = /^Ref\s*(?::\s*)?(\d+)$/i.exec(source.trim());
  if (label) {
    const next = oldToNew.get(Number(label[1]));
    return next != null ? `Ref ${next}` : source;
  }
  return source;
}

/**
 * Narrative-bearing top-level report fields, listed in dominant reading
 * order. The appearance walk scans these FIRST (so inline `[Ref N]`
 * numbering tracks how a reader encounters the report top-to-bottom), then
 * falls back to a deterministic scan of the remaining serialisable fields
 * for any tokens not yet seen. Keeping the list explicit means the
 * numbering is stable across runs and matches the section render order
 * closely without coupling to the per-type section→field registry.
 */
const READING_ORDER_FIELDS: ReadonlyArray<string> = [
  'executiveSummary',
  'summary',
  'projectIntentOverview',
  'intentOverview',
  'btmOverview',
  'gridStudy',
  'electricGrid',
  'transportStudy',
  'connectivityStudy',
  'sitingNotes',
  'climateSevereWeather',
  'climateProfile',
  'structuredFindings',
  'contextualKeyFindings',
  'keyFindings',
  'risks',
  'developmentRisks',
  'recommendations',
  'conclusion',
];

/** Collect ordered narrative text fragments from a value for the
 *  appearance walk. Strings contribute directly; arrays/objects recurse in
 *  declaration order (deterministic). */
function collectTextFragments(v: unknown, out: string[], includeFindingCitations = false, parentKey?: string): void {
  if (typeof v === 'string') {
    out.push(v);
    return;
  }
  if (Array.isArray(v)) {
    for (const item of v) collectTextFragments(item, out, includeFindingCitations, parentKey);
    return;
  }
  if (v != null && typeof v === 'object') {
    const rec = v as Record<string, unknown>;
    for (const [key, val] of Object.entries(rec)) {
      if ((key === 'gridLedStandbyPlanningAtGen' || key === 'inactiveGenerationImpactAuthorityAtGen') || (parentKey === 'qaFindings' && (key === 'sourceRoleReview' || key === 'finalSourceReview'))) continue;
      collectTextFragments(val, out, includeFindingCitations, key);
      if (includeFindingCitations && key === 'evidence') {
        for (const n of findingEvidence(rec)?.refIds ?? []) out.push(`[Ref ${n}]`);
      }
    }
  }
}

/**
 * Build the body appearance order for a report by scanning the
 * reading-order fields first, then any remaining fields, so EVERY cited ref
 * is discovered while the dominant ordering tracks the reader's path.
 */
export function collectReportAppearanceOrder(report: SiteRagReport): number[] {
  const rec = report as unknown as Record<string, unknown>;
  const fragments: string[] = [];
  const visited = new Set<string>();
  for (const key of READING_ORDER_FIELDS) {
    if (key in rec) {
      visited.add(key);
      collectTextFragments(rec[key], fragments, true, key);
    }
  }
  // Remaining fields in declaration order — excludes `references` itself
  // (its `source` strings are not body citations) so a ref's own text can't
  // claim an appearance slot. Also excludes `claims` (QA-W7 #3): the claims
  // ledger snapshots the model's ORIGINAL response — its `[Ref N]` tokens are
  // pre-renumber contract numbers and its sentences may have been rewritten
  // out of the body by the review loop. Letting stale ledger tokens drive
  // appearance order (and the cited-flag re-derivation below) marked refs as
  // "cited" that the FINAL body never cites. Claims are renumbered in
  // lock-step separately and reconciled against the final narrative by
  // `reconcileClaimsAgainstNarrative`.
  for (const [key, val] of Object.entries(rec)) {
    if (visited.has(key) || key === 'references' || key === 'claims' || (key === 'gridLedStandbyPlanningAtGen' || key === 'inactiveGenerationImpactAuthorityAtGen')) continue;
    collectTextFragments(val, fragments, true, key);
  }
  return collectRefAppearanceOrder(fragments);
}

export interface DeduplicateReferencesResult {
  references: DataSource[];
  oldToDeduped: Map<number, number>;
}

function normRefSource(source: string | undefined): string {
  return String(source ?? '').replace(/\s*\(cached\)$/i, '').replace(/\s+/g, ' ').trim();
}

function normRefUrl(url: string | undefined): string {
  return String(url ?? '').trim().replace(/\/+$/, '');
}

/**
 * Deduplicate genuinely identical entries in `references[]` using a stable
 * canonical identity, and merge unlocated bare `field: "economicEvidence"`
 * water-methodology entries into their located
 * `field: "economicEvidence.waterUseScreening.*"` counterparts at the same URL
 * when both represent the same source and qualification. Distinct documents,
 * versions, page/section locators, and access-date provenance remain separate.
 */
export function deduplicateCanonicalReferences(
  references: DataSource[],
  _appearanceOrder: number[] = [],
): DeduplicateReferencesResult {
  const n = references.length;
  const aliasOld = new Map<number, number>();
  const mergedCitedFlags = new Map<number, boolean>();

  for (let i = 0; i < n; i++) {
    const ref = references[i]!;
    if (String(ref.field ?? '').trim() !== 'economicEvidence') continue;
    const baseSource = normRefSource(ref.source);
    const baseUrl = normRefUrl(ref.url);
    if (!baseSource || !baseUrl || baseSource.includes('(')) continue;
    const candidates: number[] = [];
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const other = references[j]!;
      if (!String(other.field ?? '').trim().startsWith('economicEvidence.waterUseScreening.')) continue;
      if (normRefUrl(other.url) !== baseUrl) continue;
      if (String(other.documentId ?? '').trim() !== String(ref.documentId ?? '').trim()) continue;
      if (String(other.pageNumber ?? '') !== String(ref.pageNumber ?? '')) continue;
      if (String(other.dataAsOf ?? '').trim() !== String(ref.dataAsOf ?? '').trim()) continue;
      if (String(other.retrievedAt ?? '').trim() !== String(ref.retrievedAt ?? '').trim()) continue;
      if (String(other.qualification ?? '').trim() !== String(ref.qualification ?? '').trim()) continue;
      if (String(other.sourceGrade ?? '').trim() !== String(ref.sourceGrade ?? '').trim()) continue;
      if (Boolean(other.needsVerification) !== Boolean(ref.needsVerification)) continue;
      if (String(other.demonstrationDisclosure ?? '').trim() !== String(ref.demonstrationDisclosure ?? '').trim()) continue;
      if (JSON.stringify(other.subItems ?? null) !== JSON.stringify(ref.subItems ?? null)) continue;
      const otherSource = normRefSource(other.source);
      if (otherSource === baseSource || otherSource.startsWith(`${baseSource} (`)) {
        candidates.push(j);
      }
    }
    if (candidates.length === 1) {
      const targetIdx = candidates[0]!;
      aliasOld.set(i + 1, targetIdx + 1);
      if (ref.citedInThisReport) {
        mergedCitedFlags.set(targetIdx + 1, true);
      }
    }
  }

  const seenByKey = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    const oldIdx = i + 1;
    if (aliasOld.has(oldIdx)) continue;
    const ref = references[i]!;
    const key = [
      String(ref.field ?? '').trim(),
      normRefSource(ref.source),
      normRefSource((ref as { title?: string }).title),
      normRefUrl(ref.url),
      String(ref.documentId ?? '').trim(),
      String(ref.pageNumber ?? ''),
      String(ref.dataAsOf ?? '').trim(),
      String(ref.retrievedAt ?? '').trim(),
      String(ref.qualification ?? '').trim(),
      String(ref.sourceGrade ?? '').trim(),
      String(Boolean(ref.needsVerification)),
      String(ref.demonstrationDisclosure ?? '').trim(),
      JSON.stringify(ref.subItems ?? null),
    ].join('\u0000');
    const existingOldIdx = seenByKey.get(key);
    if (existingOldIdx !== undefined) {
      aliasOld.set(oldIdx, existingOldIdx);
      if (ref.citedInThisReport) {
        mergedCitedFlags.set(existingOldIdx, true);
      }
    } else {
      seenByKey.set(key, oldIdx);
    }
  }

  if (aliasOld.size === 0) {
    const identity = new Map<number, number>();
    for (let i = 1; i <= n; i++) identity.set(i, i);
    return { references, oldToDeduped: identity };
  }

  const dedupedRefs: DataSource[] = [];
  const survivingOldToDeduped = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    const oldIdx = i + 1;
    if (aliasOld.has(oldIdx)) continue;
    const ref = references[i]!;
    const nextRef = mergedCitedFlags.get(oldIdx) && !ref.citedInThisReport
      ? { ...ref, citedInThisReport: true }
      : ref;
    dedupedRefs.push(nextRef);
    survivingOldToDeduped.set(oldIdx, dedupedRefs.length);
  }

  const oldToDeduped = new Map<number, number>();
  for (let oldIdx = 1; oldIdx <= n; oldIdx++) {
    let targetOld = oldIdx;
    while (aliasOld.has(targetOld)) {
      targetOld = aliasOld.get(targetOld)!;
    }
    oldToDeduped.set(oldIdx, survivingOldToDeduped.get(targetOld)!);
  }

  return { references: dedupedRefs, oldToDeduped };
}

/**
 * Single finalize pass: deduplicate canonical references and renumber a
 * report's references by body appearance order, rewriting every inline
 * `[Ref N]` token, the references array order, and the claim / finding
 * source labels in lock-step.
 *
 * Pure — returns a NEW report object; the input is not mutated. When there
 * are no references (or none are cited) the report is returned with
 * references reordered to the stable (identity) plan and tokens untouched.
 */
export function renumberReportReferences(report: SiteRagReport): SiteRagReport {
  const references = report.references ?? [];
  if (references.length === 0) return report;

  const rawAppearanceOrder = collectReportAppearanceOrder(report);
  const { references: dedupedRefs, oldToDeduped } = deduplicateCanonicalReferences(references, rawAppearanceOrder);
  const appearanceOrder: number[] = [];
  const seenDeduped = new Set<number>();
  for (const oldIdx of rawAppearanceOrder) {
    const dedupedIdx = oldToDeduped.get(oldIdx) ?? oldIdx;
    if (!seenDeduped.has(dedupedIdx)) {
      seenDeduped.add(dedupedIdx);
      appearanceOrder.push(dedupedIdx);
    }
  }
  const plan = buildRenumberPlan(appearanceOrder, dedupedRefs);
  const composedOldToNew = new Map<number, number>();
  for (let oldIdx = 1; oldIdx <= references.length; oldIdx++) {
    const dedupedIdx = oldToDeduped.get(oldIdx) ?? oldIdx;
    const finalIdx = plan.oldToNew.get(dedupedIdx) ?? dedupedIdx;
    composedOldToNew.set(oldIdx, finalIdx);
  }

  // APPENDIX-POPULATE (gen-time root cause). markCitations ran BEFORE this
  // renumber pass, against pre-renumber indices/tokens — so when its
  // (best-effort) reviewer signal was empty and its literal-token scan
  // missed (compound `[Ref 2, Ref 8]`, sparse/high indices), it mis-flagged
  // rows `citedInThisReport: false` even though the FINAL body cites them.
  // This pass has already proved which refs appear in the body
  // (`plan.citedNewIndices`), so re-derive the flag here as the authority:
  // a ref that appears in the renumbered narrative IS cited. Refs that do
  // NOT appear keep `false` (genuinely uncited tails). Pure: returns NEW row
  // objects, never mutates the input rows. The appendix render reads this
  // flag, so this is what actually repopulates a mis-marked report.
  const stampCited = (refs: DataSource[]): DataSource[] =>
    refs.map((ref, i) => {
      const cited = plan.citedNewIndices.has(i + 1);
      if (ref.citedInThisReport === cited) return ref;
      return { ...ref, citedInThisReport: cited };
    });

  // QA-W7 #3c — the persisted `citationAudit` was computed PRE-renumber
  // (reportQualityPipeline Phase 4b, against geminiData INCLUDING the stale
  // claims ledger), so its `orphanedRefs` indices never matched the final
  // renumbered artifact (live evidence: refs 1-12 cited in the body yet
  // listed as orphaned). This pass has the ground truth — re-derive the
  // orphan map from `plan.citedNewIndices` so the audit describes what
  // actually ships. `totalCitations` (token count) is preserved: renumbering
  // is a bijection over tokens.
  const rebuildAudit = (audit: CitationAudit | undefined): CitationAudit | undefined => {
    if (!audit) return audit;
    const total = plan.references.length;
    const orphanedRefs: number[] = [];
    for (let n = 1; n <= total; n++) {
      if (!plan.citedNewIndices.has(n)) orphanedRefs.push(n);
    }
    return {
      ...audit,
      totalRefs: total,
      uniqueCitations: plan.citedNewIndices.size,
      orphanedCount: orphanedRefs.length,
      orphanedRefs,
    };
  };

  // Deep-rewrite tokens across the whole report EXCEPT the references array
  // (its rows are reordered, not token-rewritten — a row's `source` text is
  // a citation target description, not a body citation). Even when the
  // numbering plan is the identity map, running rewriteRefTokensDeep collapses
  // adjacent duplicate [Ref N] tokens while preserving the strongest
  // uncertainty qualification.
  const { references: _refs, claims: _claims, ...rest } = report as SiteRagReport & {
    claims?: Array<{ sources?: string[] }>;
  };
  const rewrittenRest = rewriteRefTokensDeep(rest, composedOldToNew) as Record<string, unknown>;

  // Remap claim source tokens (`ref:N` / `Ref N`) so claim lineage stays
  // aligned with the new inline numbering. QA-W7 #3b — ALSO rewrite inline
  // `[Ref N]` tokens inside claim TEXTS through the same map: the deep
  // rewrite above deliberately excludes `claims` (the rows are restructured
  // here), but the claim sentences quote the narrative and carry the same
  // pre-renumber contract numbers — leaving them untouched shipped ledgers
  // whose bracketed numbers pointed at the WRONG final references (live
  // evidence: a water-stress claim ending "[Ref 40]" resolving to an ASHRAE
  // cooling row).
  const claims = (report as SiteRagReport & { claims?: Array<{ text?: string; sources?: string[] }> }).claims;
  const remappedClaims = Array.isArray(claims)
    ? claims.map(c => ({
        ...c,
        ...(typeof c.text === 'string' && c.text.length > 0
          ? { text: rewriteRefTokens(c.text, composedOldToNew) }
          : {}),
        ...(Array.isArray(c.sources)
          ? { sources: Array.from(new Set(c.sources.map(s => remapSourceToken(s, composedOldToNew)))) }
          : {}),
      }))
    : claims;

  // Finding `source` labels (e.g. "Ref 4") are bare labels, not `[Ref N]`
  // body tokens, so the deep token rewrite above does not touch them. Remap
  // them explicitly so the Key-Findings "Source: Ref N" caption (when not
  // suppressed) tracks the new numbering.
  const remapFindingSources = (
    findings: unknown,
  ): unknown => {
    if (!Array.isArray(findings)) return findings;
    return findings.map(f => {
      if (!f || typeof f !== 'object') return f;
      const rec = f as Record<string, unknown>;
      if (typeof rec.source !== 'string') return f;
      return { ...rec, source: remapSourceToken(rec.source, composedOldToNew) };
    });
  };
  if ('structuredFindings' in rewrittenRest) {
    rewrittenRest.structuredFindings = remapFindingSources(rewrittenRest.structuredFindings);
  }
  if ('contextualKeyFindings' in rewrittenRest) {
    rewrittenRest.contextualKeyFindings = remapFindingSources(rewrittenRest.contextualKeyFindings);
  }

  const rebuiltAudit = rebuildAudit(report.citationAudit);
  return {
    ...(rewrittenRest as unknown as SiteRagReport),
    referenceNumberingVersion: 2,
    references: stampCited(plan.references),
    ...(remappedClaims !== undefined ? { claims: remappedClaims } : {}),
    ...(rebuiltAudit !== undefined ? { citationAudit: rebuiltAudit } : {}),
  } as SiteRagReport;
}

/* ── QA-W7 #3a — claims-ledger reconciliation (CT6 ghost-drop) ──────────────
 *
 * The claims ledger is extracted from the model's ORIGINAL response, but the
 * review loop (reportQualityPipeline) rewrites narrative sections in place
 * across up to 4 iterations BEFORE persist. Claims whose quoted sentence was
 * rewritten away become "ghosts": their text matches nothing in the final
 * artifact, their inline citations point nowhere a reader can verify, and the
 * citationDensity KPI keeps self-reporting against sentences that no longer
 * exist (live evidence: 13/19 ghost claims on a shipped report whose density
 * read 100%). These helpers run at persist time, AFTER the final review
 * iteration and the reference renumber above, and drop any claim whose text
 * no longer appears in the persisted sections.
 *
 * The text match deliberately mirrors the CT6 renderer matcher in
 * `walkClaimsText.tsx` (which imports `normaliseClaimTextForMatch` from here
 * so the two stay in lock-step): a claim the renderer could never attach a
 * marker to is exactly the claim this pass removes.
 */

/**
 * CT6 normalised-match key: whitespace collapsed, trailing punctuation
 * softened, lower-cased. Single source of truth for BOTH the render-time
 * sentence matcher (`walkClaimsText`) and the persist-time ghost-drop.
 */
export function normaliseClaimTextForMatch(s: string): string {
  return s
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?"'`]+$/g, '')
    .trim();
}

/** Report keys that are NOT user-facing narrative — quoting one of these
 *  must not keep a ghost claim alive (e.g. `quarantinedOriginal` holds text
 *  that was deliberately REMOVED from the report; `sedText` is input, not
 *  output). `references`/`claims` are excluded for the same reasons as the
 *  appearance walk. */
const CLAIM_MATCH_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  'references',
  'claims',
  // A frozen semantic snapshot copies claims from an earlier report body.
  // It cannot prove that those sentences still occur in the final narrative.
  'decisionAnalysis',
  'sedText',
  'quarantinedOriginal',
  'citationAudit',
  'reviewTrace',
  'sanityFindings',
  'qaFindings',
  'generationTelemetry',
  'documents',
  'documentRefs',
]);

/**
 * Collect the report's narrative surface as ONE normalised haystack string
 * for claim matching. Fragments are normalised individually (so the CT6
 * trailing-punctuation softening applies per fragment) and joined with a
 * separator that can never occur inside a normalised fragment.
 */
export function collectClaimMatchText(report: SiteRagReport): string {
  const rec = report as unknown as Record<string, unknown>;
  const fragments: string[] = [];
  for (const [key, val] of Object.entries(rec)) {
    if (CLAIM_MATCH_EXCLUDED_KEYS.has(key)) continue;
    collectTextFragments(val, fragments);
  }
  return fragments
    .map(normaliseClaimTextForMatch)
    .filter(f => f.length > 0)
    .join(' \n ');
}

export interface ReconcileClaimsResult<T> {
  /** Claims that survived (always a NEW array — input not mutated). */
  claims: T[];
  /** Number of ghost claims dropped. 0 ⇒ `claims` is a copy of the input. */
  droppedCount: number;
}

/**
 * Drop claims whose quoted `text` no longer appears in the persisted
 * narrative. Entries with empty `text` survive unconditionally — those are
 * CSE structured field-claims (`text: ''` by contract), not sentence quotes,
 * and the CT6 renderer already ignores them.
 *
 * `matchText` is the normalised haystack from `collectClaimMatchText`,
 * built from the FINAL (post-renumber) report so claim tokens and body
 * tokens share the same numbering when compared.
 */
export function reconcileClaimsAgainstNarrative<T extends { text?: unknown }>(
  claims: ReadonlyArray<T> | undefined,
  matchText: string,
): ReconcileClaimsResult<T> {
  if (!Array.isArray(claims) || claims.length === 0) {
    return { claims: claims ? [...claims] : [], droppedCount: 0 };
  }
  const kept: T[] = [];
  let droppedCount = 0;
  for (const c of claims) {
    const text = typeof c?.text === 'string' ? c.text : '';
    if (text.trim().length === 0) {
      kept.push(c); // CSE structured entry — not a sentence quote.
      continue;
    }
    const norm = normaliseClaimTextForMatch(text);
    if (norm.length > 0 && matchText.includes(norm)) kept.push(c);
    else droppedCount += 1;
  }
  return { claims: kept, droppedCount };
}
